import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { authService } from '../services/authService';
import { saveTask, removeTask, getTasksForComponent } from '../services/taskPersistence';
import { describeMeetingState, isMeetingActive, describeJoinError, meetingCanStop } from '../lib/meetBot';
import { SUPPORTED_PLATFORMS } from '../lib/recognition/routeLink';
import { useMeetingPlatforms } from '../hooks/useMeetingPlatforms';
import { ENDPOINTS } from '../config/endpoints';

// Прокси-путь до bit-meetbot: в dev через src/setupProxy.js, в prod через
// nginx.conf.template (location /meetbot-api/) — оба заведены по образцу /billing-api.
const API_BASE_URL = ENDPOINTS.meetbot;
const POLL_INTERVAL_MS = 3000;

// 🔴 Своё время жизни, а не общий час taskPersistence: встреча идёт до четырёх
// часов (bit-meetbot MAX_MEETING_SEC), после неё ещё диаризация, а сервис
// помнит встречу сутки (MEETING_TASK_TTL_SEC). С общим часом вкладка,
// перезагруженная посреди встречи, забывала бы про неё навсегда — и человек
// звал второго бота на ту же встречу.
const MEETING_TTL_MS = 24 * 60 * 60 * 1000;
const PERSIST_KEY = 'meetbot';

// Фича-флаг сборки, по умолчанию ВЫКЛЮЧЕН.
// Забытая переменная должна означать «фичи не видно», а не
// «фича сломана у всех», поэтому сравнение с 'true', а не отрицание —
// как у REACT_APP_BILLING_CARD_ENABLED в services/billingService.js.
// Переменные REACT_APP_* вшиваются на этапе СБОРКИ (CI пишет .env из
// $ENV_prod перед docker build), так что включение — строка в окружении
// сборки плюс пересборка фронта, а не перезапуск контейнера.
export const MEETBOT_ENABLED = process.env.REACT_APP_MEETBOT_ENABLED === 'true';

// Площадки, которые понимает сервис (bit-meetbot/app/platforms/registry.py),
// названы прямо в подписи: человеку иначе неоткуда узнать, сработает ли его
// ссылка, — он вставит её, получит отказ и решит, что фича сломана. Подпись
// берётся у бота (GET /platforms, hooks/useMeetingPlatforms): площадки
// включает админка. SUPPORTED_PLATFORMS — запасная подпись того же списка.
export { SUPPORTED_PLATFORMS };

/**
 * Панель «Бот на встрече»: вставить ссылку → бот заходит гостем, объявляет
 * о записи и после встречи кладёт в Историю расшифровку с именами участников.
 *
 * Протокол (саммари) на этом пути пока не делается — обещать его нельзя,
 * вкладки «Протокол» у такой записи в просмотрщике Истории не будет.
 *
 * Платформа определяется сервисом по самой ссылке — поле не спрашивает
 * пользователя, какая это площадка (бэкенд: platforms/registry.py).
 */
const MeetBotJoin = ({ onFinished }) => {
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [joinError, setJoinError] = useState(null);
  const [meetingId, setMeetingId] = useState(null);
  const [meeting, setMeeting] = useState(null); // { state, error, task_id }
  // Площадки — включённые в админке сейчас; до ответа бота — запасной список.
  const { text: platformsText } = useMeetingPlatforms();
  const [stopping, setStopping] = useState(false);

  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const pollStatus = async (id) => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/meetings/${encodeURIComponent(id)}`, {
        headers: authService.getAuthHeaders(),
      });
      setMeeting((prev) => {
        // 🔴 Список Истории обновляем ровно на ПЕРЕХОДЕ в конечное состояние,
        // а не на каждом тике: запись создаёт сам сервис на сервере, фронт о
        // ней иначе не узнаёт и показывает список без неё, пока человек не
        // перезагрузит страницу. На каждом тике был бы лишний запрос списка
        // всю встречу, а один раз — ровно когда есть что показать.
        const wasActive = prev == null || isMeetingActive(prev.state);
        if (wasActive && !isMeetingActive(data.state) && onFinished) onFinished();
        return data;
      });
      if (!isMeetingActive(data.state)) {
        stopPolling();
        removeTask(id); // встреча кончилась — восстанавливать больше нечего
      }
    } catch (_) {
      // Опрос best-effort: одиночный сбой сети не должен стирать последнее
      // известное состояние — просто попробуем на следующем тике.
    }
  };

  // Подхват встречи, начатой до перезагрузки вкладки. Опрос заводим сразу и
  // тут же спрашиваем состояние: за время, пока вкладка была закрыта, встреча
  // могла закончиться — тогда первый же ответ снимет её с отслеживания.
  useEffect(() => {
    const [saved] = getTasksForComponent(PERSIST_KEY);
    if (saved) {
      setMeetingId(saved.taskId);
      setMeeting({ state: 'JOINING', error: null, task_id: null });
      pollStatus(saved.taskId);
      pollRef.current = setInterval(() => pollStatus(saved.taskId), POLL_INTERVAL_MS);
    }
    return stopPolling; // на размонтировании — не опрашивать в никуда
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = url.trim();
    if (!value || submitting) return;

    setSubmitting(true);
    setJoinError(null);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/join`, { url: value }, {
        headers: authService.getAuthHeaders(),
      });
      setMeetingId(data.meeting_id);
      setMeeting({ state: data.state || 'JOINING', error: null, task_id: null,
                   can_stop: data.can_stop, can_start_new: data.can_start_new });
      saveTask({
        taskId: data.meeting_id, component: PERSIST_KEY,
        startedAt: new Date().toISOString(), ttlMs: MEETING_TTL_MS,
      });
      pollRef.current = setInterval(() => pollStatus(data.meeting_id), POLL_INTERVAL_MS);
    } catch (err) {
      setJoinError(describeJoinError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleStop = async () => {
    if (!meetingId || stopping) return;
    setStopping(true);
    try {
      await axios.post(`${API_BASE_URL}/meetings/${encodeURIComponent(meetingId)}/stop`, {}, {
        headers: authService.getAuthHeaders(),
      });
      await pollStatus(meetingId); // подхватить состояние сразу, не ждать тика
    } catch (_) {
      // best-effort: следующий тик опроса всё равно покажет актуальное состояние
    } finally {
      setStopping(false);
    }
  };

  const handleReset = () => {
    stopPolling();
    if (meetingId) removeTask(meetingId);
    setMeetingId(null);
    setMeeting(null);
    setJoinError(null);
    setUrl('');
  };

  return (
    <div className="rounded-panel bg-paper-card p-4 space-y-2 mb-4 font-montserrat">
      <h3 className="text-sm font-semibold text-paper-ink">Бот на встрече</h3>

      {!meetingId ? (
        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="flex gap-2">
            <Input
              type="url"
              value={url}
              onChange={(e) => { setUrl(e.target.value); setJoinError(null); }}
              placeholder="Ссылка на встречу"
              disabled={submitting}
              className="flex-1"
            />
            <Button type="submit" disabled={submitting || !url.trim()}>
              {submitting ? 'Подключаем…' : 'Позвать бота'}
            </Button>
          </div>
          <p className="text-xs text-paper-ink-3">
            Бот зайдёт на встречу, объявит о записи и пришлёт расшифровку
            с именами участников. Поддержаны {platformsText}.
          </p>
          {joinError && <p className="text-sm text-paper-accent">{joinError}</p>}
        </form>
      ) : (
        <div className="space-y-2">
          <p className={`text-sm ${meeting?.state === 'ERROR' ? 'text-paper-accent' : 'text-paper-ink-2'}`}>
            {describeMeetingState(meeting?.state, meeting?.error)}
          </p>
          {/* Кнопки — по ответу сервиса (can_stop / can_start_new), не по
              списку состояний: пока готовится расшифровка, бот уже вышел. */}
          {meetingCanStop(meeting) && (
            <Button variant="outline" size="sm" onClick={handleStop} disabled={stopping}>
              {stopping ? 'Останавливаем…' : 'Остановить'}
            </Button>
          )}
          {!meetingCanStop(meeting) && meeting?.state && (
            <Button variant="outline" size="sm" onClick={handleReset}>
              Записать ещё одну встречу
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Точка входа наружу — за флагом сборки. Гейт вынесен в отдельный компонент,
 * а не в ранний return внутри MeetBotJoin, чтобы не заводить условие перед
 * хуками.
 */
const MeetBotJoinGate = (props) => (MEETBOT_ENABLED ? <MeetBotJoin {...props} /> : null);

export default MeetBotJoinGate;
